import type { Order, PrintClaim } from '../../types'
import { PRINT_CLAIM_TTL_MS } from '../printClaim'

export const PENDING_PATCH_MAX_AGE_MS = 30_000

export interface PendingOrderPatch {
  patch: Partial<Order>
  at: number
  /** When `printClaims` in this patch were last written (TTL is 120s, not 30s). */
  printClaimsAt?: number
}

function slotClaimMatchesServer(pending: PrintClaim, server: PrintClaim | undefined): boolean {
  if (!server) return false
  const pendingId = pending.claimId?.trim()
  const serverId = server.claimId?.trim()
  if (pendingId && serverId) return pendingId === serverId
  return pending.byDeviceId === server.byDeviceId && pending.at === server.at
}

/** True when server row reflects all fields present in the optimistic patch. */
export function patchSatisfiedByServer(patch: Partial<Order>, server: Order): boolean {
  if (patch.status != null && server.status !== patch.status) return false
  if (patch.items != null && JSON.stringify(patch.items) !== JSON.stringify(server.items)) {
    return false
  }
  if (
    patch.stationProcessing != null &&
    JSON.stringify(patch.stationProcessing) !== JSON.stringify(server.stationProcessing)
  ) {
    return false
  }
  if (patch.printClaims) {
    for (const [slot, claim] of Object.entries(patch.printClaims)) {
      if (!slotClaimMatchesServer(claim, server.printClaims?.[slot])) return false
    }
  }
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) continue
    if (
      key === 'status' ||
      key === 'items' ||
      key === 'stationProcessing' ||
      key === 'printClaims'
    ) {
      continue
    }
    if ((server as unknown as Record<string, unknown>)[key] !== value) return false
  }
  return true
}

export function mergePendingPatches(
  existing: PendingOrderPatch | undefined,
  patch: Partial<Order>,
  nowMs: number,
): PendingOrderPatch {
  const printClaims =
    existing?.patch.printClaims || patch.printClaims
      ? { ...existing?.patch.printClaims, ...patch.printClaims }
      : undefined
  const { printClaims: _incoming, ...restPatch } = patch
  const hasNonClaim = Object.keys(restPatch).length > 0
  return {
    patch: {
      ...existing?.patch,
      ...patch,
      ...(printClaims ? { printClaims } : {}),
    },
    at: hasNonClaim ? nowMs : (existing?.at ?? nowMs),
    printClaimsAt: patch.printClaims ? nowMs : existing?.printClaimsAt,
  }
}

export function applyPendingPatchToOrder(base: Order, patch: Partial<Order>): Order {
  const { printClaims, ...rest } = patch
  const next: Order = { ...base, ...rest }
  if (printClaims) {
    next.printClaims = { ...base.printClaims, ...printClaims }
  }
  return next
}

/** Drop expired / server-satisfied fields; null when nothing remains to overlay. */
export function remainingPendingPatch(
  entry: PendingOrderPatch,
  server: Order | undefined,
  nowMs: number,
): PendingOrderPatch | null {
  const { printClaims, ...rest } = entry.patch
  const next: Partial<Order> = {}

  if (nowMs - entry.at <= PENDING_PATCH_MAX_AGE_MS) {
    Object.assign(next, rest)
  }

  const claimsAt = entry.printClaimsAt ?? entry.at
  if (printClaims && nowMs - claimsAt <= PRINT_CLAIM_TTL_MS) {
    next.printClaims = printClaims
  }

  if (Object.keys(next).length === 0) return null

  if (server) {
    const leftover = unsatisfiedPatch(next, server)
    if (!leftover) return null
    return { ...entry, patch: leftover }
  }

  return { ...entry, patch: next }
}

function unsatisfiedPatch(patch: Partial<Order>, server: Order): Partial<Order> | null {
  const leftover: Partial<Order> = {}
  const { printClaims, ...rest } = patch
  for (const [key, value] of Object.entries(rest)) {
    if (value === undefined) continue
    if (key === 'items' || key === 'stationProcessing') {
      if (JSON.stringify((server as unknown as Record<string, unknown>)[key]) !== JSON.stringify(value)) {
        ;(leftover as Record<string, unknown>)[key] = value
      }
      continue
    }
    if ((server as unknown as Record<string, unknown>)[key] !== value) {
      ;(leftover as Record<string, unknown>)[key] = value
    }
  }
  if (printClaims) {
    const remaining: Record<string, PrintClaim> = {}
    for (const [slot, claim] of Object.entries(printClaims)) {
      if (!slotClaimMatchesServer(claim, server.printClaims?.[slot])) {
        remaining[slot] = claim
      }
    }
    if (Object.keys(remaining).length > 0) leftover.printClaims = remaining
  }
  return Object.keys(leftover).length > 0 ? leftover : null
}

export function removePrintClaimFromPendingPatch(
  entry: PendingOrderPatch | undefined,
  slotKey: string,
): PendingOrderPatch | undefined {
  if (!entry?.patch.printClaims?.[slotKey]) return entry
  const printClaims = { ...entry.patch.printClaims }
  delete printClaims[slotKey]
  const { printClaims: _removed, ...rest } = entry.patch
  const patch: Partial<Order> =
    Object.keys(printClaims).length > 0 ? { ...rest, printClaims } : { ...rest }
  if (Object.keys(patch).length === 0) return undefined
  return {
    ...entry,
    patch,
    printClaimsAt: Object.keys(printClaims).length > 0 ? entry.printClaimsAt : undefined,
  }
}

/**
 * Apply server snapshot plus in-flight optimistic patches so listeners cannot
 * briefly downgrade status (or other patched fields) before updateDoc lands.
 */
export function mergeOrdersWithPendingPatches(
  serverOrders: readonly Order[],
  pending: ReadonlyMap<string, PendingOrderPatch>,
  previousCache: readonly Order[] | undefined,
  nowMs = Date.now(),
): { orders: Order[]; pending: Map<string, PendingOrderPatch> } {
  const prevById = new Map((previousCache ?? []).map((o) => [o.id, o]))
  const serverById = new Map(serverOrders.map((o) => [o.id, o]))
  const nextPending = new Map<string, PendingOrderPatch>()

  for (const [orderId, entry] of pending) {
    const server = serverById.get(orderId)
    const kept = remainingPendingPatch(entry, server, nowMs)
    if (!kept) continue

    nextPending.set(orderId, kept)
    const base = server ?? prevById.get(orderId)
    if (base) {
      serverById.set(orderId, applyPendingPatchToOrder(base, kept.patch))
    }
  }

  const orders: Order[] = []
  const seen = new Set<string>()

  for (const o of serverOrders) {
    orders.push(serverById.get(o.id) ?? o)
    seen.add(o.id)
  }

  for (const [orderId, entry] of nextPending) {
    if (seen.has(orderId)) continue
    const base = prevById.get(orderId)
    if (base) {
      orders.push(applyPendingPatchToOrder(base, entry.patch))
      seen.add(orderId)
    }
  }

  return { orders, pending: nextPending }
}

/** Prefer current cache row over a stale saveOrders payload. */
export function resolveOrderForPersist(
  order: Order,
  cache: readonly Order[] | undefined,
  pending: ReadonlyMap<string, PendingOrderPatch>,
): Order {
  const cached = cache?.find((o) => o.id === order.id)
  if (!cached) return order
  if (pending.has(order.id)) return cached
  return cached
}
